import { Moon, ShoppingCart, Sun, Menu, X, User, LayoutDashboard, ShoppingBag } from 'lucide-react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

// --- IMPORTACIONES ---
import { useThemeStore } from '../estado/useThemeStore.ts';
import { useCarrito } from '../features/carrito/useCarrito.ts';
import { useAuth } from '../features/auth/useAuth.ts';
import { setAuthToken, api } from '../lib/api.ts';

// IMPORTAMOS EL COMPONENTE DEL CARRITO LATERAL
import { CartDrawer } from '../features/carrito/CartDrawer.jsx'; 

export function NavbarPublic() {
  const { isDarkMode, toggleDarkMode } = useThemeStore();
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const { items, openCart } = useCarrito();
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { isAuthenticated, user } = useAuth(); 

  // --- CORRECCIÓN MATEMÁTICA APLICADA ---
  // 1. Items Locales: Sumamos SOLO los que pertenecen a la tienda actual (slug)
  const localItemsCount = items
    .filter(item => item.storeSlug === slug) 
    .reduce((sum, item) => sum + item.quantity, 0);

  // 2. Items Globales (Otras tiendas - Desde BD)
  const { data: globalCartCount } = useQuery({
    queryKey: ['global-cart-count-nav', slug], 
    queryFn: async () => {
        if (!isAuthenticated) return 0;
        try {
            const { data } = await api.get('/api/carrito/manage/global/');
            return Array.isArray(data) ? data.reduce((acc, cart) => {
                if (cart.store_slug === slug) return acc; 
                return acc + cart.total_items;
            }, 0) : 0;
        } catch (e) { return 0; }
    },
    enabled: isAuthenticated,
    refetchInterval: 5000,
  });

  // TOTAL REAL (Local de esta tienda + Remoto de otras tiendas)
  const totalItems = localItemsCount + (globalCartCount || 0);
  
  // LÓGICA DE VISIBILIDAD
  const isStorePage = slug && location.pathname.startsWith(`/t/${slug}`);
  const isCustomerProfile = location.pathname === '/mi-cuenta';
  const isMarketplacePage = location.pathname === '/marketplace';
  
  // Mostrar carrito si: Estamos en una tienda O estamos en el perfil O hay items cargados
  const showCart = isStorePage || isCustomerProfile || totalItems > 0;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    // Limpiar ambos tokens
    setAuthToken(null, null); 
    window.location.assign('/'); 
  };

  const renderAuthLinks = (isMobile = false) => {
    const linkClass = isMobile
      ? "flex items-center gap-3 w-full px-4 py-3 text-sm font-medium tracking-widest uppercase text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all"
      : "flex items-center gap-2 px-4 py-2 text-[10px] font-bold tracking-widest uppercase text-white/60 hover:text-white transition-colors";
    
    const ctaClass = isMobile
      ? "flex items-center justify-center gap-2 w-full px-4 py-3 mt-2 text-sm font-bold tracking-widest uppercase bg-white text-black rounded-xl shadow-lg shadow-white/10"
      : "flex items-center gap-2 px-5 py-2 text-[10px] font-bold tracking-widest uppercase bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]";

    if (isAuthenticated) {
      return (
         <>
          {/* SuperAdmin - Redirigir a panel de administración */}
          {user?.is_superuser && (
            <Link to="/super-admin" className={ctaClass} onClick={() => setIsMenuOpen(false)}>
              <LayoutDashboard className="h-3 w-3 md:h-3.5 md:w-3.5" /> 
              {isMobile ? 'Panel Admin' : 'ADMIN'}
            </Link>
          )}

          {/* Cliente - Mostrar Mi Cuenta o Marketplace */}
          {!user?.is_superuser && user?.user_type?.toLowerCase() === 'customer' && (
            isCustomerProfile ? (
                <Link to="/marketplace" className={ctaClass} onClick={() => setIsMenuOpen(false)}>
                  <ShoppingBag className="h-3 w-3 md:h-3.5 md:w-3.5" /> 
                  {isMobile ? 'Explorar Tiendas' : 'MARKETPLACE'}
                </Link>
            ) : (
                <Link to="/mi-cuenta" className={linkClass} onClick={() => setIsMenuOpen(false)}>
                  <User className="h-3 w-3 md:h-3.5 md:w-3.5" /> 
                  {isMobile ? 'Mi Cuenta' : 'MI CUENTA'}
                </Link>
            )
          )}

          {/* Merchant - Mostrar Panel */}
          {!user?.is_superuser && user?.user_type?.toLowerCase() === 'merchant' && (
            <Link to="/panel" className={ctaClass} onClick={() => setIsMenuOpen(false)}>
              <LayoutDashboard className="h-3 w-3 md:h-3.5 md:w-3.5" /> {isMobile ? 'Mi Panel' : 'PANEL'}
            </Link>
          )}

          <button
            onClick={() => {
              handleLogout();
              setIsMenuOpen(false);
            }}
            className={`${linkClass} text-red-400 hover:text-red-300`}
          >
            {isMobile ? 'Cerrar Sesión' : <span className="text-[10px]">SALIR</span>}
          </button>
        </>
      );
    }
    
    return (
      <>
        <Link to="/inicio-sesion" className={linkClass} onClick={() => setIsMenuOpen(false)}>
          INICIAR SESIÓN
        </Link>
        <Link to="/registro" className={ctaClass} onClick={() => setIsMenuOpen(false)}>
          CREAR CUENTA
        </Link>
      </>
    );
  };

  return (
    <>
      <CartDrawer />

      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled || isMenuOpen
            ? 'bg-[#050505]/80 backdrop-blur-xl border-white/5 py-3' 
            : 'bg-transparent border-transparent py-6'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
          
          {/* LOGO MODIFICADO */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.png" 
              alt="TiendasYA Logo" 
              fetchPriority="high"
              width="100"
              height="56"
              className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>
          
          {/* NAVEGACIÓN */}
          <div className="flex items-center gap-6">
            
            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center gap-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 text-white/40 hover:text-white transition-colors"
                aria-label="Toggle theme"
              >
                {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              
              {/* BOTÓN MARKETPLACE */}
              {!isMarketplacePage && !isCustomerProfile && (
                <Link 
                  to="/marketplace" 
                  className="flex items-center gap-2 px-4 py-2 text-[10px] font-bold tracking-widest uppercase text-white/60 hover:text-white transition-colors"
                >
                  <ShoppingBag className="h-4 w-4" />
                  MARKETPLACE
                </Link>
              )}

              {/* CARRITO DESKTOP */}
              {showCart && (
                <button
                  onClick={openCart}
                  className="relative p-2 text-white/60 hover:text-white transition-colors group"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {totalItems > 0 && (
                    <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-black shadow-lg group-hover:scale-110 transition-transform">
                      {totalItems}
                    </span>
                  )}
                </button>
              )}

              <div className="h-4 w-px bg-white/10 mx-2"></div>

              {renderAuthLinks(false)}
            </nav>

            {/* Mobile Toggle */}
            <div className="flex md:hidden items-center gap-4">
              
              {/* CARRITO MOBILE */}
              {showCart && (
                <button 
                  onClick={openCart}
                  className="relative text-white"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {totalItems > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-black">
                      {totalItems}
                    </span>
                  )}
                </button>
              )}
              
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white p-1"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Menú Móvil */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-[#050505] border-b border-white/10 p-6 animate-in slide-in-from-top-5">
            <nav className="flex flex-col gap-2">
              {/* Botón Marketplace en Móvil */}
              {!isMarketplacePage && !isCustomerProfile && (
                 <Link 
                    to="/marketplace" 
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium tracking-widest uppercase text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                    onClick={() => setIsMenuOpen(false)}
                 >
                    <ShoppingBag className="h-4 w-4" />
                    Explorar Marketplace
                 </Link>
              )}

              {renderAuthLinks(true)}
              <div className="h-px w-full bg-white/5 my-4"></div>
              <button
                onClick={toggleDarkMode}
                className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium tracking-widest uppercase text-white/50 hover:text-white hover:bg-white/5 rounded-xl transition-all"
              >
                 {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} 
                 {isDarkMode ? 'Modo Claro' : 'Modo Oscuro'}
              </button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}